/**
 * Pure builders for the agents-island pill **clickCta** (src:198 evid 10) `additionalFields`.
 * Shared contract with native `AgentsIslandClickBi` (iOS/Android) — keep keys identical.
 *
 * The pill mounts on many screens, so `page_name` / `pageName` is the CURRENT screen
 * (`biPageName` / `Route.biName`), never a hardcoded "agents-island". `component_name` names
 * the action: navigate to Your Agents vs open the quick-view. Island state rides
 * `additionalFields` so funnels can still slice by pill colour / counts.
 */

export const AGENTS_ISLAND_COMPONENT = 'agents_island';
export const AGENTS_ISLAND_QUICKVIEW_COMPONENT = 'agents_island_quickview';

/** `maestro_working` is the Maestro mid-turn in the user's own chat. It gets its OWN token rather than
 *  the colour underneath it: the pill wears the blue in-motion chip but is not reporting task work, and
 *  folding it into `in_progress` would silently inflate that state's share with turns that have no task
 *  behind them at all. Shared 1:1 with iOS/Android `AgentsIslandClickBi.pillState`. */
export type AgentsIslandPillState = 'green' | 'in_progress' | 'waiting' | 'agent_started' | 'maestro_working' | 'action_completed';

export type AgentsIslandClickFieldsInput = {
  pillState: AgentsIslandPillState;
  inMotionCount: number;
  /** TASKS waiting on the user — the historical meaning of `waiting_count`, shared 1:1 with iOS/Android,
   *  the widgets and the Watch. Never redefine it: every existing funnel slices on it. */
  waitingCount: number;
  /** The CARDS the island now shows and counts — one per open question, plus one per waiting task with no
   *  question. Lower than `waitingCount` whenever one question blocks several tasks, which is why it has
   *  its own field instead of replacing one: the two answer different questions about the same pill. */
  waitingQuestions: number;
  /** The PEOPLE half of the orange pill — the NEEDS_YOU threads (`native-apps/shared/outreach-needs-you/contract.json`
   *  rule 1: "the pill BI adds `waiting_people`"). Absent from a caller that has no such count. */
  waitingPeople?: number;
  agentCount: number;
  skillsCount: number;
};

/** Flat context for the agents-island pill tap — empty values dropped. */
export function agentsIslandClickFields(input: AgentsIslandClickFieldsInput): Record<string, string> {
  const out: Record<string, string> = {
    pill_state: input.pillState,
    in_motion_count: String(input.inMotionCount),
    waiting_count: String(input.waitingCount),
    waiting_questions: String(input.waitingQuestions),
    waiting_people: input.waitingPeople === undefined ? '' : String(input.waitingPeople),
    agent_count: String(input.agentCount),
    skills_count: String(input.skillsCount),
    origin: 'header',
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== ''));
}

export function agentsIslandClickComponent(opensQuickView: boolean): string {
  return opensQuickView ? AGENTS_ISLAND_QUICKVIEW_COMPONENT : AGENTS_ISLAND_COMPONENT;
}
